import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { SingleDatePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import styled, { css } from "styled-components";
import CrossIcon from "sweet-web/components/icons/CrossIcon";
import { colors } from "sweet-web/lib/styles";
import { RootState } from "sweet-web/redux";
import { searchFilter } from "sweet-web/redux/search/actions";
import * as selectors from "sweet-web/redux/selectors";

type WrapperProps = {
  width: string;
  active: boolean;
  hasValue: boolean;
};
const Wrapper = styled.div<WrapperProps>`
  width: ${props => props.width};
  /* width: auto; */
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  height: ${props => (props.active ? 60 : 32)}px;
  /* overflow: hidden; */
  box-sizing: border-box;
  padding: 5px;
  border-radius: ${props => (props.active ? 5 : 0)}px;
  background-color: ${colors.white};
  border: 1px solid
    ${props =>
      props.active || props.hasValue ? colors.primary : colors.lightGray};
  &:hover {
    box-shadow: rgba(0, 0, 0, 0.08) 0 3px 10px 0;
    border: 1px solid ${props => (props.active ? colors.primary : colors.gray)};
    p {
      color: ${colors.primary};
    }
  }
  @media screen and (max-width: 600px) {
    /* mobile styling */
    border-radius: 5px;
    margin: 3px 5px 5px 5px;
  }
`;

const DateWrapper = styled.div<{ visible: boolean }>`
  display: ${props => (props.visible ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  &:hover {
    cursor: default;
  }
`;

type LabelProps = {
  active: boolean;
  hasValue: boolean;
};
const Label = styled.span<LabelProps>`
  outline: none;
  white-space: nowrap;
  border: none;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  height: 14px;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  color: ${props =>
    props.active || props.hasValue ? colors.primary : colors.nearBlack};
  border-radius: 5px;
  margin-top: 3px;
  padding: 0 10px;
  margin-top: ${props => (props.active ? 5 : 3)}px;
  &:hover {
    cursor: default;
  }
`;

const Form = styled.form<{ visible: boolean }>`
  margin-top: ${props => (props.visible ? 8 : 0)}px;
  height: ${props => (props.visible ? "auto" : "0")};
`;

type InputProps = {
  width: number;
  visible: boolean;
};
const Input = styled.input<InputProps>`
  border-radius: 5px;
  outline: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  color: ${colors.primary};
  pointer-events: ${props => (props.visible ? "auto" : "none")};
  opacity: ${props => (props.visible ? 1 : 0)};
  /* flex: 1; */
  width: ${props => props.width}px;
  /* margin: 0 10px 10px 10px; */
  box-sizing: border-box;
  /* background-color: rgba(255, 255, 255, 0.3); */
  background-color: transparent;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &::placeholder {
    color: ${colors.gray};
  }
`;

const RemoveIcon = styled.div`
  position: absolute;
  width: 14px;
  height: 14px;
  right: 15px;
  padding: 5px;
  top: 3px;
`;

const Prefix = styled.p<{ visible: boolean }>`
  color: ${colors.primary};
  font-size: 14px;
  font-weight: 600;
  position: absolute;
  margin: 3px 0 0 10px;
  display: ${props => (props.visible ? "block" : "none")};
`;

type ReduxProps = ConnectedProps<typeof connector>;
type OwnProps = {
  title: string;
  name: string;
  type: "text" | "number" | "date" | "select";
  display?: (value: any) => any;
  label?: (value: any) => any;
  normalize?: (value: any) => any;
  width?: number;
  placeholder: string;
  prefix?: string;
  style?: object;
};
type Props = ReduxProps & OwnProps;
const InputFilter: React.FC<Props> = props => {
  const labelRef = useRef(null);
  const inputRef = useRef(null);

  const [labelWidth, setLabelWidth] = useState("auto");
  const [focus, setFocus] = useState(false);
  const [value, setValue] = useState(
    null as string | number | null | moment.Moment
  );

  // update value if redux value has changed
  useEffect(() => {
    if (props.filter && props.filter !== value) setValue(props.filter);
    else if (!props.filter && props.filter !== value) setValue(null);
  }, [props.filter]);

  const updateFilterValue = (newValue: any) => {
    if (props.filter !== newValue && newValue)
      props.searchFilter({ [props.name]: newValue });
    else if (props.filter && !newValue) {
      props.searchFilter({ [props.name]: null });
    }
  };

  // calculate width of label and input for dynamic sizing
  useEffect(() => {
    if (labelRef.current) {
      setLabelWidth(`${labelRef.current.offsetWidth + 10}px`);
    }
  }, [value, focus]);
  const width = focus ? `${props.width}px` : labelWidth;

  return (
    <Wrapper
      width={width}
      hasValue={Boolean(value)}
      key={`filter-${props.name}`}
      className={"sweet-discover-filter"}
      active={focus}
      onClick={() => {
        if (props.type != "date") inputRef.current.focus();
        if (props.type == "date") setFocus(true);
      }}
      style={props.style}
    >
      <Label
        ref={labelRef}
        active={focus}
        hasValue={Boolean(value)}
        style={props.type === "select" ? { paddingRight: 25 } : {}}
      >
        {value && !focus ? props.label(value) : props.title}
        {props.type === "select" && (
          <RemoveIcon
            ref={inputRef}
            onClick={() => {
              updateFilterValue(null);
            }}
          >
            <CrossIcon size={14} color={colors.primary} />
          </RemoveIcon>
        )}
      </Label>
      <Form
        onSubmit={e => {
          e.preventDefault();
          setFocus(false);
          updateFilterValue(value);
        }}
        visible={focus}
      >
        {props.prefix && <Prefix visible={focus}>{props.prefix}</Prefix>}
        {(props.type === "text" || props.type === "number") && (
          <Input
            ref={inputRef}
            onFocus={e => {
              e.target.select();
              setFocus(true);
            }}
            width={props.width}
            type={props.type}
            value={value ? props.display(value) : ""}
            placeholder={props.placeholder}
            onChange={e => {
              setValue(props.normalize(e.target.value));
            }}
            onBlur={() => {
              setFocus(false);
              updateFilterValue(value);
            }}
            visible={focus}
          />
        )}
        {props.type === "date" && (
          <DateWrapper visible={focus}>
            <SingleDatePicker
              id={`discover-filter-date-picker`}
              date={value ? moment(value) : null}
              numberOfMonths={1}
              focused={focus}
              onDateChange={date => {
                const normalizedDate = props.normalize(date);
                updateFilterValue(normalizedDate);
              }}
              onFocusChange={e => {
                setFocus(e.focused);
              }}
              placeholder={props.placeholder}
              small={true}
              hideKeyboardShortcutsPanel={true}
              showClearDate={true}
            />
          </DateWrapper>
        )}
      </Form>
    </Wrapper>
  );
};
InputFilter.defaultProps = {
  display: value => value,
  label: value => value,
  normalize: value => value,
  style: {}
};

const mapState = (state: RootState, ownProps: OwnProps) => ({
  filter: selectors.searchFilter(ownProps.name)(state)
});
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      searchFilter
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default connector(InputFilter);
